<template>
<div>

  <div class="dashboard-block" v-if="$paymentsModuleEnabled && $userIsAdmin">
      <div class="alert alert-icon alert-info" v-if="bookingBookedDepositUnpaid">
        <i class="fa fa-exclamation"/>
        Deposit unpaid
      </div>

      <div class="alert alert-icon alert-info" v-if="bookingBookedDepositSent">
        <i class="fa fa-exclamation"/>
        Deposit sent but not marked as received
      </div>
  </div>

  <div>
    <h2 v-if="$userIsClient">What happens next</h2>

    <booking-alert-terms-and-conditions v-if="!$vlastMode && bookingTermsAreNotAccepted && $userIsClient"></booking-alert-terms-and-conditions>

    <campaign-notification
      v-if="isShoutout && !booking.video_access_token && ( $userIsClient )"
      theme="light"
      icon="check-circle"
      title="Video Shout-outs"
      :has-buttons="false"
    >
      <template v-slot:summary>
        <ul>
          <li>The {{ $starLabel }} will aim to have your video to you within 3 days.</li>
          <li>If for some reason they can't get it to you within 10 days, PickStar will process your refund</li>
          <li>You will receive an email and text when your video is ready for download, as well as progress update emails</li>
        </ul>
      </template>
    </campaign-notification>

    <campaign-notification
      v-else-if="isShoutout && booking.video_access_token"
      theme="light"
      icon="bullhorn"
      title="View your shoutout"
      :has-buttons="false"
    >
      <template v-slot:summary>
        Click <a target="_blank" class="cursor-pointer" :href="videoLink">here</a> to view
      </template>
    </campaign-notification>

    <campaign-notification
      v-if="isShoutout && !booking.video_access_token && $userIsTalent"
      theme="light"
      icon="check-circle"
      title="Video Shout-outs"
      :heading="`To complete this booking, you'll need to use the ${this.clientName} app.`"
      :app-links="true"
      :has-buttons="false"
    />

    <div
      v-if="$paymentsModuleEnabled && (!$user.has_mobile_app || bookingPaymentDepositIsUnpaid) && !isShoutout && $userIsClient"
      class="cta-card"
    >
      <img class="cta-card__image margin-none" src="~assets/img/chat-unlocked.jpg">
      <div class="flex flex-col cta-card__body">
        <div v-if="bookingPaymentDepositIsUnpaid">
          <div class="spacer-b-2">
            <p class="cta-card__heading"><strong>Confirm the booking to chat directly to your {{ $starLabel }}!</strong></p>
            <p class="cta-card__sub">Pay your deposit, download our Mobile App and chat to the {{ $starLabel }}.</p>
          </div>
          <router-link class="btn btn-primary btn-sm" style="width: fit-content" :to="{
    name: 'payments.show',
    params: {
      bookingId: booking.id,
      action: 'process',
      paymentId: bookingPaymentDeposit.id,
      props: { default: true, showMessage: false }
    } }">
            Pay Now
          </router-link>
        </div>
        <div v-else>
          <div class="spacer-b-2">
            <p class="cta-card__heading"><strong>Your booking is confirmed, time to chat to the {{ $starLabel }} about the details!</strong></p>
            <p class="cta-card__sub">Download our Mobile App and chat to the {{ $starLabel }} now!</p>
          </div>
          <div class="flex items-center">
            <a href="https://itunes.apple.com/au/app/pickstar-sports-talent/id1425719956?mt=8" target="_blank" >
              <img alt='Download in App Store' src='https://linkmaker.itunes.apple.com/en-us/badge-lrg.svg?releaseDate=2018-10-06&kind=iossoftware&bubble=ios_apps' style="height: 40px; width: auto; max-width: none;"/>
            </a>
            <a href="https://play.google.com/store/apps/details?id=pickstar.com.au.talent&hl=en_AU" target="_blank" >
              <img alt='Get it on Google Play' src='https://play.google.com/intl/en_us/badges/images/generic/en_badge_web_generic.png' style="height: 60px; width: auto; max-width: none;"/>
            </a>
          </div>
        </div>
      </div>
    </div>

    <BookingAlertUnpaidFinal
      v-if="$paymentsModuleEnabled && !bookingPaymentDepositIsUnpaid &&bookingPaymentFinalIsUnpaid && $userIsClient">
    </BookingAlertUnpaidFinal>

    <booking-terms-and-conditions-modal
      v-if="!$vlastMode"
      ref="terms"
      :booking="booking"
      :talents="bookingAttendingTalent"
      :property-mutator="setRequestProperty">
    </booking-terms-and-conditions-modal>

  </div>

  <div class="alert alert-danger alert-icon" v-if="bookingTypeIsBookingRequest && bookingHasExpired">
    <i class="fa fa-frown-o"></i>
    <span v-if="bookingHaveNoAppliedTalent">
      You did not have any responses in time. Feel free to change your request or contact the {{ clientName }} team for advice.
    </span>
    <span v-else>
      Unfortunately your booking has expired. Contact the {{ clientName }} team for advice.
    </span>
  </div>
</div>

</template>

<script>
import { helpers, paymentHelpers } from 'mixins/bookings'
import { filter, get } from 'lodash'
import BookingAlertTermsAndConditions from './BookingAlertTermsAndConditions'
import BookingTermsAndConditionsModal from './BookingTermsAndConditionsModal'
import BookingTermsAndConditionsModalNoAction from './BookingTermsAndConditionsModalNoAction'
import BookingAlertUnpaidDeposit from './BookingAlertUnpaidDeposit'
import BookingAlertUnpaidFinal from './BookingAlertUnpaidFinal'
import CampaignNotification from './../campaign/CampaignNotification'
import { publicUrl } from 'utils'

export default {
  mixins: [helpers, paymentHelpers],

  components: {
    BookingAlertTermsAndConditions,
    BookingTermsAndConditionsModal,
    BookingTermsAndConditionsModalNoAction,
    BookingAlertUnpaidDeposit,
    BookingAlertUnpaidFinal,
    CampaignNotification
  },

  props: {
    booking: {
      type: Object,
      required: true
    }
  },

  computed: {
    videoLink () {
      if (this.booking && this.booking.video_access_token) {
        return publicUrl('buy/video-shoutouts/download?accessToken=' + this.booking.video_access_token, this.$user.region.shortname)
      }
      return null
    },

    /**
     * Determine if the booking has no applied talent.
     *
     * @return bool
     */
    bookingHaveNoAppliedTalent () {
      return !this.bookingHaveAppliedTalent
    },

    /**
     * @returns {boolean}
     */
    isShoutout () {
      return this.booking.package_id === 1
    },

    /**
     * Determine if the booking has applied talent.
     *
     * @return bool
     */
    bookingHaveAppliedTalent () {
      return filter(this.booking.talent, talent => get(talent, 'response.status') === 'applied').length > 0
    },

    /**
     * Alert class for the request is vetted progress step.
     *
     * @return string
     */
    alertClassRequestIsVetted () {
      if (this.bookingIsVetting) {
        return 'alert-info'
      }

      return 'alert-success'
    },

    /**
     * Icon class for the request is vetted progress step.
     *
     * @return string
     */
    iconClassRequestIsVetted () {
      if (this.bookingIsVetting) {
        return 'fa-minus'
      }

      return 'fa-check'
    },

    /**
     * Alert class for the talent reviewing request progress step.
     *
     * @return string
     */
    alertClassTalentReviewingRequest () {
      if (this.bookingIsVetting) {
        return 'alert-default'
      } else if (this.bookingIsNotVetting && this.bookingTypeIsBookingRequest && this.bookingHaveNoAppliedTalent) {
        return 'alert-info'
      }

      return 'alert-success'
    },

    /**
     * Icon class for the talent reviewing request progress step.
     *
     * @return string
     */
    iconClassTalentReviewingRequest () {
      if (this.bookingIsVetting || this.bookingHaveNoAppliedTalent) {
        return 'fa-minus'
      }

      return 'fa-check'
    },

    /**
     * Alert class for the talent chosen progress step.
     *
     * @return string
     */
    alertClassTalentChosen () {
      if (this.bookingHasAllSelectedTalent) {
        return 'alert-success'
      } else if (this.bookingIsVetting || (this.bookingTypeIsBookingRequest && this.bookingHaveNoAppliedTalent)) {
        return 'alert-default'
      } else if (this.bookingTypeIsBookingRequest && this.bookingHaveAppliedTalent) {
        return 'alert-info'
      }

      return 'alert-success'
    },

    /**
     * Icon class for the talent chosen progress step.
     *
     * @return string
     */
    iconClassTalentChosen () {
      if (this.bookingHasAllSelectedTalent) {
        return 'fa-check'
      }
      return 'fa-minus'
    },

    /**
     * Alert class for the terms and conditions and deposit paid progress step.
     *
     * @return string
     */
    alertClassTermsAcceptedAndDepositPaid () {
      if (this.bookingHasAllSelectedTalent && this.bookingPaymentDepositIsUnpaid) {
        return 'alert-info'
      } else if (this.bookingIsVetting || this.bookingTypeIsBookingRequest) {
        return 'alert-default'
      } else if (this.bookingTermsAreNotAccepted || this.bookingPaymentDepositIsUnpaid) {
        return 'alert-info'
      }

      return 'alert-success'
    },

    /**
     * Alert class for the booking status shown to admin users
     *
     * @return string
     */
    alertClassBookingStatus () {
      if (this.bookingIsVetting) {
        return 'alert-info'
      } else if (this.bookingPaymentDepositIsUnpaid || this.bookingPaymentFinalIsUnpaid) {
        return 'alert-info'
      }

      return 'alert-info'
    },

    /**
     * Icon class for the terms and conditions and deposit paid progress step.
     *
     * @return string
     */
    iconClassTermsAcceptedAndDepositPaid () {
      if (this.bookingIsVetting || this.bookingTypeIsBookingRequest || this.bookingTermsAreNotAccepted || this.bookingPaymentDepositIsUnpaid) {
        return 'fa-info'
      }

      return 'fa-check'
    },

    /**
     * Alert class for the final payment progress step.
     *
     * @return string
     */
    alertClassFinalPaymentPaid () {
      if (this.bookingTypeIsBooking && this.bookingPaymentDepositIsPaid && this.bookingPaymentFinalIsPaid) {
        return 'alert-success'
      } else if (this.bookingTypeIsBooking && this.bookingPaymentDepositIsPaid && this.bookingPaymentFinalIsUnpaid) {
        return 'alert-info'
      }

      return 'alert-info'
    },

    /**
     * Icon class for the final payment progress step.
     *
     * @return string
     */
    iconClassFinalPaymentPaid () {
      if (this.bookingIsVetting || this.bookingTypeIsBookingRequest || this.bookingPaymentFinalIsUnpaid) {
        return 'fa-minus'
      }

      return 'fa-check'
    }
  }
}
</script>

<style lang="scss">
.cta-card {
  background-color: white;
  padding: 0px 20px;
  margin-bottom: 20px;
  font-size: 16px;
  display: flex;
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    align-items: center;
  }
  &__image {
    max-height: 200px;
    height: auto;
    object-fit: cover;
  }
  &__body {
    @media (max-width: 768px) {
      padding-top: 20px;
    }
    @media (min-width: 768px) {
      padding: 20px;
    }
  }
  &__sub {
    color: #71717a;
  }
  &__heading {
    text-transform: uppercase;
  }
}
</style>
